import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TKFilter from '@tk/components/tk.filter';

export default class TKFilterGroupWrapper extends TKCustomElementFactory {
    tkFilter?: TKFilter;
    groupsShowingCount: number = 0;
    groupCount: number = 0;

    private resizeObserver: ResizeObserver;
    private collapseButton?: HTMLButtonElement;
    private expandButton?: HTMLButtonElement;
    private moreGroupWrapperClassName: string;
    private closeDrilldownButtons: NodeListOf<HTMLButtonElement>;
    private drilldownClassName: string;
    private loadedClassName: string;

    constructor() {
        super();
        this.tkFilter = this.closest('tk-filter') || undefined;
        this.moreGroupWrapperClassName = (
            this.getAttribute('data-tk-filter-group-wrapper-more-class')
            || 'tk-filter-group-wrapper--expand'
        );
        this.collapseButton = this.querySelector('[data-tk-filter-group-collapse]') || undefined;
        this.expandButton = this.querySelector('[data-tk-filter-group-expand]') || undefined;
        this.closeDrilldownButtons = this.querySelectorAll('[data-tk-close-drilldown]') || undefined;
        this.resizeObserver = new ResizeObserver(this.checkResize.bind(this));
        this.drilldownClassName = (
            this.getAttribute('data-tk-drilldown-class-name')
            || 'tk-filter-group-wrapper--drilldown'
        );
        this.loadedClassName = this.getAttribute('data-tk-loaded-class-name') || 'tk-filter-group-wrapper--loaded';
    }

    connectedCallback(): void {
        if (!this.tkFilter || !this.collapseButton || !this.expandButton) return;
        this.resizeObserver.observe(this);
        this.attachClickEventListener();
        this.classList.add(this.loadedClassName);
    }

    attachClickEventListener() {
        const handleCollapseGroups = this.collapseFilterGroups.bind(this);
        this.pushListener({ event: 'click', element: this.collapseButton!, action: handleCollapseGroups });
        const handleExpandGroups = this.expandFilterGroups.bind(this);
        this.pushListener({ event: 'click', element: this.expandButton!, action: handleExpandGroups });
        const handleCloseDrilldown = this.closeDrilldown.bind(this);
        this.closeDrilldownButtons.length > 0 && this.closeDrilldownButtons.forEach((button) => {
            this.pushListener({ event: 'click', element: button, action: handleCloseDrilldown });
        });
    }

    collapseFilterGroups() {
        this.classList.remove(this.moreGroupWrapperClassName);
        this.hideCollapseButton();
        this.showExpandButton();
    }

    expandFilterGroups() {
        this.classList.add(this.moreGroupWrapperClassName);
        this.hideExpandButton();
        this.showCollapseButton();
        this.showAllGroups();
    }

    closeDrilldown() {
        this.classList.remove(this.drilldownClassName);
        this.tkFilter?.post();
    }

    showExpandButton(): void {
        if (!this.expandButton) return;
        this.expandButton.hidden = false;
    }

    hideExpandButton(): void {
        if (!this.expandButton) return;
        this.expandButton.hidden = true;
    }

    showCollapseButton(): void {
        if (!this.collapseButton) return;
        this.collapseButton.hidden = false;
    }

    hideCollapseButton(): void {
        if (!this.collapseButton) return;
        this.collapseButton.hidden = true;
    }

    showAllGroups() {
        const childrenList = this.getFilteredGroups();
        childrenList.forEach((element) => {
            element.hidden = false;
        });
    }

    checkResize(entries: ResizeObserverEntry[]) {
        entries.forEach(() => {
            this.checkGroupLength();
        });
    }

    checkGroupLength() {
        this.showAllGroups();
        this.optimizeGroups();
    }

    optimizeGroups() {
        if (this.classList.contains('tk-filter-group-wrapper--drilldown')) return;
        this.expandButton && (this.expandButton.hidden = true);
        let { scrollWidth } = this;
        const { width, right } = this.getBoundingClientRect();
        if (width >= scrollWidth) return;
        scrollWidth = this.scrollWidth;
        const childrenList = this.getFilteredGroups();
        const closestHiddenChild = childrenList.find((child) => {
            const childRect = child.getBoundingClientRect();
            const childRight = childRect.right;
            return right < childRight;
        });
        if (!closestHiddenChild) return;
        this.expandButton && (this.expandButton.hidden = false);
        const index = childrenList.indexOf(closestHiddenChild);
        this.groupsShowingCount = index - 1;
        this.trimGroupsToMaxCount(childrenList, this.groupsShowingCount);
    }

    getFilteredGroups() : HTMLElement[] {
        return Array.from(this.querySelectorAll('tk-filter-group'));
    }

    trimGroupsToMaxCount(childrenList: HTMLElement[], maxShowingGroup: number) {
        const difference = this.groupCount - maxShowingGroup;
        const lastChildren = childrenList.slice(difference * (-1));
        lastChildren.forEach((element) => {
            element.hidden = true;
        });
    }
}
